/**
 * Priority Delivery Assistance — optional paid service in the cart.
 *
 * Adds or removes a single unit of the assistance product, records the customer's
 * consent on the line item, then re-renders every cart section the same way theme.js
 * does (GET /cart?view=... and swap the .Cart node), so Liquid stays the single
 * source of truth for what the cart shows.
 *
 * Rules from the French Pharmacy developer instructions:
 *   - never added automatically, never restored after the customer removes it
 *   - charged once per order (quantity is forced back to 1)
 *   - removed when no eligible physical merchandise is left
 *   - consent timestamp, fee base, fee, currency and policy version are stored with
 *     the line item, and a cart attribute exposes the selection to customer service
 */
(function () {
  'use strict';

  var BUSY = false;
  var ATTRIBUTE = 'Priority Delivery Assistance';

  function ajax(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    });
  }

  function toArray(nodeList) {
    return Array.prototype.slice.call(nodeList);
  }

  function closest(element, selector) {
    while (element && element.nodeType === 1) {
      if (element.matches && element.matches(selector)) {
        return element;
      }
      element = element.parentElement;
    }
    return null;
  }

  function attributeNumber(element, name) {
    if (!element) {
      return 0;
    }
    return parseInt(element.getAttribute(name), 10) || 0;
  }

  /* Let the currency converter re-run on the markup we just injected. */
  function refreshCurrency() {
    ['cart:updated', 'cart:refresh', 'currency:refresh', 'doubly:refresh'].forEach(function (name) {
      try {
        document.dispatchEvent(new CustomEvent(name, { bubbles: true }));
      } catch (error) {
        /* Nothing listening, or the event could not be built: not a problem. */
      }
    });

    [
      function () { window.Doubly.convert(); },
      function () { window.doubly.convert(); },
      function () { window.Currency.convertAll(); }
    ].forEach(function (hook) {
      try {
        hook();
      } catch (error) {
        /* That converter is not on this page, or it refreshes itself. */
      }
    });
  }

  /**
   * The service is hidden from the cart UI, so it is left out of the header badge too —
   * a customer with two products should not see a 3. Mirrors the same subtraction in
   * sections/header.liquid.
   */
  function visibleItemCount(cart) {
    if (!cart || typeof cart.item_count !== 'number') {
      return null;
    }

    var hidden = assistanceLines(cart).reduce(function (total, line) {
      return total + line.quantity;
    }, 0);

    return Math.max(cart.item_count - hidden, 0);
  }

  function updateHeaderCount(cart) {
    var count = visibleItemCount(cart);

    if (count === null) {
      return;
    }

    toArray(document.querySelectorAll('.Header__CartCount')).forEach(function (element) {
      element.textContent = count;
      element.classList.toggle('is-empty', count === 0);
    });

    toArray(document.querySelectorAll('.Header__CartDot')).forEach(function (element) {
      if (count === 0) {
        element.classList.remove('is-visible');
      } else {
        element.classList.add('is-visible');
      }
    });
  }

  function refreshSection(section) {
    var options = {};

    try {
      options = JSON.parse(section.getAttribute('data-section-settings')) || {};
    } catch (error) {
      options = {};
    }

    var view = options.drawer ? 'drawer' : 'ajax';

    return fetch('/cart?view=' + view + '&timestamp=' + Date.now(), {
      credentials: 'same-origin',
      method: 'GET'
    }).then(function (response) {
      return response.text();
    }).then(function (html) {
      var temporary = document.createElement('div');
      temporary.innerHTML = html;

      var freshCart = temporary.querySelector('.Cart');
      var currentCart = section.querySelector('.Cart');

      /* The cart went empty (or the response was unexpected): let the theme redraw it. */
      if (!freshCart || !currentCart || !currentCart.parentNode) {
        window.location.reload();
        return;
      }

      var scroller = section.querySelector('.Drawer__Main');
      var scrollPosition = scroller ? scroller.scrollTop : 0;

      currentCart.parentNode.replaceChild(freshCart, currentCart);

      scroller = section.querySelector('.Drawer__Main');
      if (scroller) {
        scroller.scrollTop = scrollPosition;
      }

      var freshHeader = temporary.querySelector('.PageHeader');
      var currentHeader = section.querySelector('.PageHeader');
      if (freshHeader && currentHeader) {
        currentHeader.innerHTML = freshHeader.innerHTML;
      }

      refreshCurrency();
    });
  }

  function refreshCarts() {
    var sections = toArray(document.querySelectorAll('[data-section-type="cart"]'));

    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (response) {
      return response.json();
    }).then(function (cart) {
      updateHeaderCount(cart);
      return Promise.all(sections.map(refreshSection));
    });
  }

  function assistanceHandle() {
    return (window.theme && window.theme.assistanceHandle) || 'priority-delivery-assistance';
  }

  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (response) {
      return response.json();
    });
  }

  function assistanceLines(cart) {
    var handle = assistanceHandle();
    if (!cart || !cart.items) {
      return [];
    }
    return cart.items.filter(function (item) {
      return item.handle === handle;
    });
  }

  /* Change several lines one after another — Shopify line keys shift if sent in parallel. */
  function changeLines(changes) {
    return changes.reduce(function (chain, change) {
      return chain.then(function () {
        return ajax('/cart/change.js', change);
      });
    }, Promise.resolve());
  }

  /* Exposes the selection to customer service on the order. */
  function setCartAttribute(value) {
    var attributes = {};
    attributes[ATTRIBUTE] = value;
    return ajax('/cart/update.js', { attributes: attributes }).catch(function (error) {
      console.warn('[cart-assistance] could not record the cart attribute', error);
    });
  }

  function eligibleTotal(cart) {
    var handle = assistanceHandle();
    if (!cart || !cart.items) {
      return 0;
    }
    return cart.items.reduce(function (total, item) {
      if (item.handle !== handle && item.requires_shipping && !item.gift_card) {
        return total + item.final_line_price;
      }
      return total;
    }, 0);
  }

  /**
   * Adds one unit of the service and records consent with the line.
   * options: { variantId, feeBase, feeCents, currency, policyVersion }
   */
  function addAssistance(options) {
    var settings = options || {};
    var variantId = parseInt(settings.variantId, 10) ||
      parseInt(window.theme && window.theme.assistanceVariantId, 10);

    if (!variantId) {
      return Promise.reject(new Error('No assistance variant to add'));
    }

    var consentAt = new Date().toISOString();
    var policyVersion = settings.policyVersion ||
      (window.theme && window.theme.assistancePolicyVersion) || '';

    return getCart().then(function (cart) {
      var feeBase = typeof settings.feeBase === 'number' ? settings.feeBase : eligibleTotal(cart);
      var currency = settings.currency || cart.currency || '';
      var feeCents = settings.feeCents || (window.theme && window.theme.assistanceFee) || '';

      /* Underscore-prefixed properties are stored with the order but not shown to the customer. */
      return ajax('/cart/add.js', {
        id: variantId,
        quantity: 1,
        properties: {
          _pda_consent_at: consentAt,
          _pda_fee_base_cents: String(feeBase),
          _pda_fee_cents: String(feeCents),
          _pda_currency: currency,
          _pda_policy_version: policyVersion
        }
      });
    }).then(function (response) {
      if (!response.ok) {
        throw new Error('Could not add assistance, status ' + response.status);
      }
      return setCartAttribute('Selected ' + consentAt + ' (policy ' + (policyVersion || 'n/a') + ')');
    });
  }

  function removeAssistance() {
    return getCart().then(function (cart) {
      var lines = assistanceLines(cart);

      if (!lines.length) {
        return setCartAttribute('');
      }

      return changeLines(lines.map(function (line) {
        return { id: line.key, quantity: 0 };
      })).then(function () {
        return setCartAttribute('');
      });
    });
  }

  function onToggle(input) {
    if (BUSY) {
      input.checked = !input.checked;
      return;
    }

    var wrapper = closest(input, '[data-cart-assistance]');
    var isSelecting = input.checked;

    if (isSelecting && !parseInt(input.getAttribute('data-variant-id'), 10)) {
      return;
    }

    if (!isSelecting && !input.getAttribute('data-line-id')) {
      return;
    }

    BUSY = true;
    input.disabled = true;
    if (wrapper) {
      wrapper.classList.add('is-loading');
    }
    document.dispatchEvent(new CustomEvent('theme:loading:start'));

    var request = isSelecting
      ? addAssistance({
          variantId: input.getAttribute('data-variant-id'),
          feeBase: attributeNumber(wrapper, 'data-eligible-total'),
          feeCents: attributeNumber(input, 'data-fee-cents'),
          currency: wrapper ? wrapper.getAttribute('data-currency') : '',
          policyVersion: wrapper ? wrapper.getAttribute('data-policy-version') : ''
        })
      : removeAssistance();

    request.then(function () {
      return refreshCarts();
    }).catch(function (error) {
      console.error('[cart-assistance]', error);

      /* Put the checkbox back where it was so it still matches the cart. */
      input.checked = !isSelecting;
      input.disabled = false;
      if (wrapper) {
        wrapper.classList.remove('is-loading');
      }
    }).then(function () {
      BUSY = false;
      document.dispatchEvent(new CustomEvent('theme:loading:end'));
    });
  }

  document.addEventListener('change', function (event) {
    var input = closest(event.target, '[data-cart-assistance-toggle]');
    if (input) {
      onToggle(input);
    }
  });

  /**
   * Housekeeping, run whenever a cart is drawn. The service is charged once per order, so:
   *  - more than one assistance line (one from the cart page, one from the Rebuy drawer)
   *    collapses back to a single line
   *  - a quantity above 1 goes back to 1
   *  - the line is dropped when no eligible physical merchandise is left
   */
  function tidyUp() {
    if (BUSY) {
      return;
    }

    getCart().then(function (cart) {
      var lines = assistanceLines(cart);

      if (!lines.length) {
        return;
      }

      var eligible = 0;
      cart.items.forEach(function (item) {
        if (item.handle !== assistanceHandle() && item.requires_shipping && !item.gift_card) {
          eligible += item.final_line_price;
        }
      });

      var changes = [];

      /* Nothing left to protect: the offer is not even shown, so drop the line. */
      if (eligible === 0) {
        changes = lines.map(function (line) {
          return { id: line.key, quantity: 0 };
        });
      } else {
        /* Prefer the line that carries the consent record; otherwise keep the first. */
        var keep = lines.filter(function (line) {
          return line.properties && line.properties._pda_consent_at;
        })[0] || lines[0];

        /* Keep one line at quantity 1, drop any duplicates. */
        lines.forEach(function (line) {
          if (line.key === keep.key) {
            if (line.quantity !== 1) {
              changes.push({ id: line.key, quantity: 1 });
            }
          } else {
            changes.push({ id: line.key, quantity: 0 });
          }
        });
      }

      /**
       * A line added from the Rebuy drawer carries no consent properties, because that
       * toggle is Rebuy's and cannot attach them. Record what we still can — the
       * selection, the moment it was noticed and the policy version — on the cart
       * attribute, so the order is not left with no record at all.
       */
      var kept = lines.filter(function (line) {
        return line.properties && line.properties._pda_consent_at;
      })[0];

      if (eligible > 0 && !kept && !(cart.attributes || {})[ATTRIBUTE]) {
        var version = (window.theme && window.theme.assistancePolicyVersion) || 'n/a';
        setCartAttribute('Selected in cart drawer, seen ' + new Date().toISOString() +
                         ' (policy ' + version + ')');
      }

      if (!changes.length) {
        return;
      }

      BUSY = true;
      return changeLines(changes).then(function () {
        if (eligible === 0) {
          return setCartAttribute('');
        }
      }).then(function () {
        BUSY = false;
        return refreshCarts();
      }).catch(function (error) {
        BUSY = false;
        console.warn('[cart-assistance] housekeeping failed', error);
      });
    }).catch(function (error) {
      console.warn('[cart-assistance] housekeeping skipped', error);
    });
  }

  /* Nudge the Rebuy drawer to redraw after we change the cart behind its back. */
  function refreshRebuy() {
    try {
      var smartCart = window.Rebuy && window.Rebuy.SmartCart;
      if (!smartCart) {
        return;
      }
      if (typeof smartCart.setCartAsync === 'function') {
        smartCart.setCartAsync();
      } else if (typeof smartCart.reInit === 'function') {
        smartCart.reInit();
      }
    } catch (error) {
      console.warn('[cart-assistance] could not refresh the Rebuy cart', error);
    }
  }

  /**
   * Public API, used by the toggle rendered inside the Rebuy drawer (a Smart Cart custom
   * code block). Keeping the logic here means one implementation for both surfaces, and
   * it stays in the theme repo rather than only in Rebuy's dashboard.
   */
  window.CartAssistance = {
    handle: assistanceHandle,
    getCart: getCart,
    eligibleTotal: eligibleTotal,

    isSelected: function (cart) {
      return assistanceLines(cart).length > 0;
    },

    add: function (options) {
      return addAssistance(options).then(function () {
        refreshRebuy();
        return refreshCarts();
      });
    },

    remove: function () {
      return removeAssistance().then(function () {
        refreshRebuy();
        return refreshCarts();
      });
    },

    isBusy: function () {
      return BUSY;
    },

    setBusy: function (value) {
      BUSY = !!value;
    }
  };

  /**
   * The toggle rendered inside the Rebuy drawer.
   *
   * The markup lives in a Smart Cart custom code block, but all of the behaviour lives
   * here: Rebuy re-injects that block's HTML on every cart render, and a <script> tag
   * injected that way does not run again, so a block that wired itself up would work once
   * and then quietly stop. Watching for the markup from the theme survives every redraw.
   */
  var DRAWER_SELECTOR = '[data-pda]';

  function renderDrawerBlock(element, cart) {
    var input = element.querySelector('[data-pda-switch]');
    if (!input) {
      return;
    }

    /* The money filter wraps the price in <span class=money>, which the currency widget
       looks for — so this is set as HTML. The value is the theme's own setting. */
    var fee = (window.theme && window.theme.assistanceFeeFormatted) || '';
    toArray(element.querySelectorAll('.pda__price')).forEach(function (price) {
      if (price.innerHTML !== fee) {
        price.innerHTML = fee;
      }
    });

    var terms = element.querySelector('[data-pda-terms]');
    if (terms && !terms.getAttribute('href')) {
      terms.setAttribute('href', (window.theme && window.theme.assistanceTermsUrl) ||
        '/pages/shipping-returns#priority-delivery-assistance');
    }

    /* No eligible physical merchandise: the offer is not made at all. */
    element.hidden = eligibleTotal(cart) === 0;
    input.checked = assistanceLines(cart).length > 0;
    element.classList.remove('is-busy');
  }

  function bindDrawerBlock(element) {
    if (element.getAttribute('data-pda-bound') === 'true') {
      return;
    }
    element.setAttribute('data-pda-bound', 'true');

    var input = element.querySelector('[data-pda-switch]');
    if (!input) {
      return;
    }

    input.addEventListener('change', function () {
      if (BUSY) {
        input.checked = !input.checked;
        return;
      }

      var selecting = input.checked;
      element.classList.add('is-busy');

      var action = selecting ? addAssistance({}) : removeAssistance();

      action.then(function () {
        refreshRebuy();
        return refreshCarts();
      }).then(function () {
        return syncDrawerBlocks();
      }).catch(function (error) {
        console.error('[cart-assistance] drawer toggle failed', error);
        input.checked = !selecting;
        element.classList.remove('is-busy');
      });
    });
  }

  function syncDrawerBlocks(cart) {
    var elements = toArray(document.querySelectorAll(DRAWER_SELECTOR));

    if (!elements.length) {
      return Promise.resolve();
    }

    elements.forEach(bindDrawerBlock);

    if (cart) {
      elements.forEach(function (element) {
        renderDrawerBlock(element, cart);
      });
      return Promise.resolve();
    }

    return getCart().then(function (freshCart) {
      elements.forEach(function (element) {
        renderDrawerBlock(element, freshCart);
      });
    }).catch(function (error) {
      console.warn('[cart-assistance] could not sync the drawer toggle', error);
    });
  }

  var drawerTimer = null;

  function syncDrawerBlocksSoon() {
    window.clearTimeout(drawerTimer);
    drawerTimer = window.setTimeout(function () { syncDrawerBlocks(); }, 150);
  }

  function watchForDrawerBlocks() {
    syncDrawerBlocks();

    new MutationObserver(function (mutations) {
      var sawBlock = mutations.some(function (mutation) {
        return toArray(mutation.addedNodes).some(function (node) {
          if (node.nodeType !== 1) {
            return false;
          }
          return (node.matches && node.matches(DRAWER_SELECTOR)) ||
            (node.querySelector && node.querySelector(DRAWER_SELECTOR));
        });
      });

      if (sawBlock) {
        syncDrawerBlocksSoon();
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  var tidyTimer = null;

  function tidyUpSoon() {
    window.clearTimeout(tidyTimer);
    tidyTimer = window.setTimeout(tidyUp, 400);
  }

  document.addEventListener('DOMContentLoaded', function () {
    tidyUp();
    watchForDrawerBlocks();
  });
  document.addEventListener('product:added', tidyUpSoon);

  /* The theme fires this after every quantity change / cart re-render. */
  document.addEventListener('theme:loading:end', tidyUpSoon);

  /**
   * The Rebuy drawer is a separate cart UI and its toggle does not know about lines this
   * theme added. Listen to its events too, so a duplicate line is collapsed straight away
   * rather than waiting for the next page load.
   */
  [
    'rebuy:cart.add',
    'rebuy:cart.change',
    'rebuy:cart.update',
    'rebuy:smartcart.show',
    'rebuy:smartcart.hide',
    'rebuy:smartcart.cart-change',
    'rebuy:smartcart.line-item-increase',
    'rebuy:smartcart.line-item-decrease'
  ].forEach(function (name) {
    document.addEventListener(name, function () {
      tidyUpSoon();
      syncDrawerBlocksSoon();
    });
  });

  if (document.readyState !== 'loading') {
    tidyUp();
    watchForDrawerBlocks();
  }
})();
